import {useCollabId, useEditorFocusManager, usePlanningId} from 'components/DSYS/CollabProvider';
import {DocumentThreadsProvider} from 'components/DSYS/editor/plugins/comments/ThreadsProvider';
import {SelectedTextControls} from 'components/DSYS/editor/plugins/SelectedTextToolbar';
import {Props as RichTextAreaProps, RichTextArea} from 'components/DSYS/RichTextArea';
import type {LexicalEditor} from 'lexical';
import {type ElementRef, forwardRef, useEffect, useMemo, useRef} from 'react';

export type PlanningCommentableRichTextProps = Omit<
  RichTextAreaProps,
  'comments' | 'commentsDocumentId' | 'selectionControls'
> & {
  selectionControls?: RichTextAreaProps['selectionControls'];
};

export const PlanningCommentableRichText = forwardRef<
  ElementRef<typeof RichTextArea>,
  PlanningCommentableRichTextProps
>(function PlanningCommentableRichText(
  {
    selectionControls,
    planningId: planningIdProp,
    autoFocus = false,
    disableAISuggestions,
    onEditorReady,
    onToggleFocused,
    ...rest
  },
  ref
) {
  const documentId = useCollabId();
  const planningIdFromContext = usePlanningId();
  const planningId = planningIdProp ?? planningIdFromContext;
  const editorRef = useRef<LexicalEditor | null>(null);
  const focusManager = useEditorFocusManager();

  const controls = useMemo((): SelectedTextControls[] | undefined => {
    const base: SelectedTextControls[] = selectionControls ?? ['comments'];
    if (documentId) {
      return base.includes('comments') ? base : [...base, 'comments'];
    }
    // Comments need a document to attach threads to. Without one (e.g. a standalone
    // workstream page), drop them; a read-only editor then has no selection controls left.
    const withoutComments = base.filter((control) => control !== 'comments');
    return rest.editable && withoutComments.length ? withoutComments : undefined;
  }, [documentId, rest.editable, selectionControls]);

  const richText = (
    <RichTextArea
      ref={ref}
      planningId={planningId}
      commentsDocumentId={documentId}
      selectionControls={controls}
      autoFocus={autoFocus}
      disableAISuggestions={Boolean(planningId) || Boolean(disableAISuggestions)}
      onToggleFocused={(focused) => {
        // Clear all other editors when this one receives focus.
        // Use editor's built-in _key as stable identifier
        if (focused && editorRef.current) {
          focusManager?.clearAllExcept(editorRef.current._key);
        }
        onToggleFocused?.(focused);
      }}
      onEditorReady={(editor) => {
        // Store editor ref and register with focus manager if available
        editorRef.current = editor;
        focusManager?.registerEditor(editor._key, editor);
        onEditorReady?.(editor);
      }}
      {...rest}
    />
  );

  // Unregister editor on unmount
  useEffect(() => {
    return () => {
      if (editorRef.current) {
        focusManager?.unregisterEditor(editorRef.current._key);
      }
    };
  }, [focusManager]);

  if (!documentId) {
    return richText;
  }

  return <DocumentThreadsProvider documentId={documentId}>{richText}</DocumentThreadsProvider>;
});
